import {
  Box,
  Checkbox,
  FormControlLabel,
  Modal,
  Typography,
} from "@mui/material";
import { FC, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import styles from "@/components/files/company/companyBatchUploadModal.module.css";
import { Controller, useForm } from "react-hook-form";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import FileUploadSelection from "@/components/forms/inputs/fileUpload/fileuploadSelection";
import GenericButton from "@/components/forms/inputs/button/genericButton";
import { api } from "@/helper/api";
import DropDown from "@/components/forms/inputs/dropDown";
import { useModalDismiss } from "@/helper/customHooks/useModalDismiss";
import useFocusFirstInvalidField from "@/helper/customHooks/useFocusFirstInvalidField";
import useFileUploadProgress, {
  FileUploadRequestOptions,
} from "@/helper/customHooks/useFileUploadProgress";
import FileUploadProgress from "@/components/forms/inputs/fileUploadProgress";

interface CompanyBatchUploadModalProps {
  modalOpen: boolean;
  onClose: () => void;
  selectedTableRows: any[];
  handleSubmitFormData: (
    data: any,
    options: FileUploadRequestOptions,
  ) => Promise<void>;
}

type CompanyBatchUploadFormValues = {
  is_exclusion_list: boolean;
  include_children: boolean;
  parents_only: boolean;
  file_set: any;
  folder_id: string;
};

const CompanyBatchUploadModal: FC<CompanyBatchUploadModalProps> = ({
  modalOpen,
  onClose,
  selectedTableRows,
  handleSubmitFormData,
}) => {
  const { t } = useTranslation();
  const {
    control,
    handleSubmit,
    reset,
    watch,
    formState: { errors },
  } = useForm<CompanyBatchUploadFormValues>({
    shouldFocusError: false,
    defaultValues: {
      is_exclusion_list: false,
      include_children: false,
      parents_only: false,
      file_set: [],
      folder_id: "",
    },
  });
  const parentsOnlySelected = watch("parents_only");
  const includeChildrenSelected = watch("include_children");
  const isExclusionList = watch("is_exclusion_list");
  const { registerValidationTarget, focusFirstInvalidField } =
    useFocusFirstInvalidField<CompanyBatchUploadFormValues>({
      fieldOrder: ["folder_id", "file_set"],
    });

  const [folderOptions, setFolderOptions] = useState<any[]>([]);
  const {
    phase,
    progressPercentage,
    startUpload,
    handleUploadProgress,
    resetUploadProgress,
  } = useFileUploadProgress();
  const uploadActive = phase !== "idle";

  useEffect(() => {
    if (modalOpen) {
      getFolders();
      reset();
      resetUploadProgress();
    }
  }, [modalOpen, reset, resetUploadProgress]);

  const getTranslationKey = () => {
    const count = selectedTableRows.length;

    if (count === 0) {
      if (parentsOnlySelected)
        return "no_companies_selected_create_batchupload_for_all_parents_only";
      return "no_companies_selected_create_batchupload_for_all";
    }

    if (isExclusionList) {
      if (parentsOnlySelected) {
        return "create_batchupload_for_all_companies_except_selected_parents_only";
      }
      if (includeChildrenSelected) {
        return "create_batchupload_for_all_companies_except_selected_include_children";
      }
      return "create_batchupload_for_all_companies_except_selected_companies";
    }

    if (parentsOnlySelected && !isExclusionList) {
      return "create_batchupload_for_selected_companies_parents_only";
    }

    if (includeChildrenSelected) {
      return "create_batchupload_for_all_selected_companies_include_children";
    }

    return "create_batchupload_for_selected_companies";
  };

  const getFolders = async () => {
    try {
      const response: any = await new Promise((resolve, reject) => {
        api.genericApiRequest({
          entity: "folder",
          method: "get",
          successHandler: (res: any) => resolve(res),
          parametersToRender: {
            additionalUrlParameters: { tree__name: "default_manage" },
          },
          failHandler: (err: any) => reject(err),
        });
      });

      const options = (response?.data?.results || []).map((folder: any) => ({
        value: folder.id,
        label: folder.name,
      }));
      setFolderOptions(options);
    } catch (err) {
      console.log(err);
    }
  };

  const onSubmit = async (data: any) => {
    const tempCompanySubjectSet =
      selectedTableRows.length > 0
        ? selectedTableRows
            .map((row: any) => row.subject?.id || row.subject_id)
            .filter(Boolean)
        : [];

    const submitData: any = {
      file_set: data.file_set,
      company_subject_set: tempCompanySubjectSet,
      is_exclusion_list: data.is_exclusion_list,
      include_children: data.include_children,
      parents_only: data.parents_only,
      folder_id: data?.folder_id?.value,
    };

    startUpload();
    try {
      await handleSubmitFormData(submitData, {
        onUploadProgress: handleUploadProgress,
      });
    } finally {
      resetUploadProgress();
    }
  };

  const cancelClose = useModalDismiss(modalOpen, onClose, {
    enabled: !uploadActive,
    allowBackdropClick: true,
  });

  return (
    <Modal
      open={modalOpen}
      {...cancelClose}
      disableAutoFocus
    >
      <Box className={styles.modalContentBoxWrapper}>
        <Typography variant="h6" component="h2" gutterBottom align="center">
          {t("upload_file")}
        </Typography>
        <form
          noValidate
          onSubmit={handleSubmit(onSubmit, (errors) => {
            focusFirstInvalidField(errors);
          })}
        >
          <Box className={styles.formContent}>
            <Controller
              name="folder_id"
              control={control}
              rules={{ required: t("field_required") as string }}
              render={({ field }) => (
                <div ref={registerValidationTarget("folder_id")}>
                  <DropDown
                    options={folderOptions}
                    useReduxDefaultValue={false}
                    label={t("folder")}
                    mandatory={true}
                    required={true}
                    disabled={uploadActive}
                    onChange={field.onChange}
                  />
                </div>
              )}
            />
            {errors.folder_id && (
              <Typography color="error" variant="caption">
                {errors.folder_id.message}
              </Typography>
            )}
            <Controller
              name="file_set"
              control={control}
              rules={{ required: t("no_file_selected") as string }}
              render={({ field }) => (
                <div ref={registerValidationTarget("file_set")}>
                  <FileUploadSelection
                    value={field.value}
                    onChange={field.onChange}
                    multiple={true}
                    disabled={uploadActive}
                  />
                </div>
              )}
            />
            {errors.file_set && (
              <Typography color="error" variant="caption">
                {String(errors.file_set.message)}
              </Typography>
            )}
            <Box className={styles.checkboxWrapper}>
              <Controller
                name="is_exclusion_list"
                control={control}
                render={({ field }) => (
                  <>
                    <FormControlLabel
                      label={t("is_exclusion_list")}
                      control={
                        <Checkbox
                          {...field}
                          value={field.value}
                          checked={field.value}
                          disabled={
                            uploadActive || selectedTableRows.length === 0
                          }
                          onChange={(event) =>
                            field.onChange(event.target.checked)
                          }
                        />
                      }
                      className={styles.modalCheckbox}
                    />
                  </>
                )}
              />
              <Controller
                name="include_children"
                control={control}
                render={({ field }) => (
                  <>
                    <FormControlLabel
                      label={t("include_child_companies")}
                      control={
                        <Checkbox
                          {...field}
                          value={field.value}
                          checked={field.value}
                          onChange={(event) =>
                            field.onChange(event.target.checked)
                          }
                          disabled={
                            uploadActive ||
                            parentsOnlySelected ||
                            selectedTableRows.length === 0
                          }
                        />
                      }
                      className={styles.modalCheckbox}
                    />
                  </>
                )}
              />
              <Controller
                name="parents_only"
                control={control}
                render={({ field }) => (
                  <>
                    <FormControlLabel
                      label={t("only_parent_companies")}
                      control={
                        <Checkbox
                          {...field}
                          value={field.value}
                          checked={field.value}
                          onChange={(event) =>
                            field.onChange(event.target.checked)
                          }
                          disabled={uploadActive || includeChildrenSelected}
                        />
                      }
                      className={styles.modalCheckbox}
                    />
                  </>
                )}
              />
            </Box>
            <div className={styles.infoContainer}>
              <InfoOutlinedIcon />
              <p className={styles.infoText}>
                {t(getTranslationKey(), { count: selectedTableRows.length })}
              </p>
            </div>
            <FileUploadProgress
              phase={phase}
              progressPercentage={progressPercentage}
              dataTestId="company-batch-upload-progress"
            />
            <Box className={styles.buttonWrapper}>
              <GenericButton
                variant="outlined"
                onClick={onClose}
                disabled={uploadActive}
              >
                {t("cancel")}
              </GenericButton>
              <GenericButton
                variant="contained"
                type="submit"
                loading={uploadActive}
                disabled={uploadActive}
              >
                {t("save")}
              </GenericButton>
            </Box>
          </Box>
        </form>
      </Box>
    </Modal>
  );
};

export default CompanyBatchUploadModal;
